import React, { useCallback, useEffect, useRef, useState } from 'react';
import RoleSection from './RoleSection';
import JoinCodesPanel from './JoinCodesPanel';
import CreateParticipantAccountDialog from './CreateParticipantAccountDialog';
import styles from '../ProjectEditForm.module.css';
import { useAuth } from '../../../providers/AuthContext';
import api from '../../../API.js';

const ROLES = ['annotator', 'reviewer', 'admin'];

function ParticipantsPanel({
    participants,
    onAddRole,
    onRemoveRole,
    isSelfRemovalLocked,
    participantError,
    participantSaving,
    folderId,
    disableJoinCodes = false,
}) {
    const [joinModalOpen, setJoinModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const openButtonRef = useRef(null);
    const createButtonRef = useRef(null);
    const dialogRef = useRef(null);
    const createDialogOpenRef = useRef(false);

    const { user } = useAuth();
    const [emailAuthPerms, setEmailAuthPerms] = useState(null); // { canCreatePasswordlessUsers, delegatedUserCreation }
    const [emailAuthPermsError, setEmailAuthPermsError] = useState(null);

    const closeJoinModal = useCallback(() => {
        setJoinModalOpen(false);
        requestAnimationFrame(() => {
            openButtonRef.current?.focus();
        });
    }, []);

    const closeCreateModal = useCallback(() => {
        setCreateModalOpen(false);
        requestAnimationFrame(() => {
            createButtonRef.current?.focus();
        });
    }, []);

    useEffect(() => {
        createDialogOpenRef.current = createModalOpen;
    }, [createModalOpen]);

    useEffect(() => {
        if (!user?._id) return;
        let cancelled = false;
        setEmailAuthPermsError(null);
        api.get('emailauth/permissions')
            .then((resp) => {
                if (cancelled) return;
                if (resp.status !== 200 || !resp.data) {
                    setEmailAuthPerms(null);
                    return;
                }
                setEmailAuthPerms({
                    canCreatePasswordlessUsers: !!resp.data.canCreatePasswordlessUsers,
                    delegatedUserCreation: !!resp.data.delegatedUserCreation,
                });
            })
            .catch(() => {
                if (cancelled) return;
                setEmailAuthPerms(null);
                setEmailAuthPermsError('Could not load emailauth permissions.');
            });
        return () => { cancelled = true; };
    }, [user?._id]);

    useEffect(() => {
        if (!joinModalOpen && !createModalOpen) return undefined;
        function onKeyDown(e) {
            if (e.key === 'Escape') {
                e.preventDefault();
                if (createDialogOpenRef.current) {
                    closeCreateModal();
                } else {
                    closeJoinModal();
                }
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [joinModalOpen, createModalOpen, closeJoinModal, closeCreateModal]);

    useEffect(() => {
        if (!joinModalOpen) return undefined;
        const id = requestAnimationFrame(() => {
            const root = dialogRef.current;
            const first = root?.querySelector('select, input, button:not([disabled])');
            first?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, [joinModalOpen]);

    function handleBackdropMouseDown(e) {
        if (e.target === e.currentTarget) {
            closeJoinModal();
        }
    }

    const isSiteAdmin = !!user?.admin;
    const isProjectAdmin = isSiteAdmin || !!participants?.some((p) => String(p?.userId) === String(user?._id) && (p.roles || []).includes('admin'));
    const canCreatePasswordlessUsers = isSiteAdmin || !!emailAuthPerms?.canCreatePasswordlessUsers;
    const createDisabled = !folderId || !isProjectAdmin || !canCreatePasswordlessUsers;
    const showPrivilegeMessage = !!folderId && isProjectAdmin && !canCreatePasswordlessUsers;

    return (
        <div className={styles.panel}>
            <div className={styles.panelTitleRow}>
                <h4 className={styles.panelTitle}>Participants</h4>
                <div className={styles.panelTitleActions}>
                    <button
                        type="button"
                        ref={openButtonRef}
                        className={styles.buttonPrimary}
                        onClick={() => setJoinModalOpen(true)}
                        disabled={!folderId || disableJoinCodes}
                        aria-haspopup="dialog"
                        aria-expanded={joinModalOpen}
                    >
                        Invite participants via access codes
                    </button>
                    <button
                        type="button"
                        ref={createButtonRef}
                        className={styles.buttonSecondary}
                        onClick={() => setCreateModalOpen(true)}
                        disabled={createDisabled}
                        aria-haspopup="dialog"
                        aria-expanded={createModalOpen}
                    >
                        Create participant account
                    </button>
                </div>
            </div>
            {emailAuthPermsError ? (
                <p className={styles.itemActionError} role="alert">{emailAuthPermsError}</p>
            ) : null}
            {showPrivilegeMessage ? (
                <p className={styles.emptyState} aria-live="polite">
                    Ask your site administrator for privileges.
                </p>
            ) : null}
            {participantSaving && (
                <p className={styles.emptyState} aria-live="polite">Updating participants…</p>
            )}
            {participantError && (
                <p className={styles.itemActionError} role="alert">{participantError}</p>
            )}
            {ROLES.map((role) => (
                <RoleSection
                    key={role}
                    role={role}
                    participants={participants}
                    onAddRole={onAddRole}
                    onRemoveRole={onRemoveRole}
                    isSelfRemovalLocked={isSelfRemovalLocked}
                    disableActions={participantSaving}
                />
            ))}

            {joinModalOpen && folderId ? (
                <div
                    className={styles.confirmOverlay}
                    role="presentation"
                    onMouseDown={handleBackdropMouseDown}
                >
                    <div
                        ref={dialogRef}
                        className={`${styles.confirmDialog} ${styles.confirmDialogJoinCodes}`}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="join-codes-modal-title"
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <h2 id="join-codes-modal-title" className={styles.confirmDialogTitle}>
                            Join codes
                        </h2>
                        <JoinCodesPanel folderId={folderId} variant="plain" />
                        <div className={styles.confirmActions}>
                            <button
                                type="button"
                                className={styles.buttonSecondary}
                                onClick={closeJoinModal}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            <CreateParticipantAccountDialog
                open={createModalOpen && !!folderId}
                folderId={folderId}
                onAddRole={onAddRole}
                onClose={closeCreateModal}
                disabled={createDisabled}
            />
        </div>
    );
}

export default React.memo(ParticipantsPanel);
